import { Button, Flex, Grid } from "@sidelinesports/fan-design-system";
import { useEffect, useState } from "react";
import { useSessionStorage } from "react-use";
import { Page } from "../../analytics/Page.tsx";
import { Dialog, FullscreenLayout, UploadedImage } from "../../components";
import { AppFooter } from "../../components/AppFooter";
import { ErrorScreen, LoadingScreen } from "../../screens";
import { GameHistoryDialog } from "../GameHistoryDialog/GameHistoryDialog.tsx";
import { GameListItem, NoGamesMessage } from "./components";
import { useGameListQueries } from "./hooks";
import { canUserViewGame } from "./rules";
import { compareGamesByStatusAndDate } from "./rules/compare-games-by-status-and-date.ts";

export const GamesListPage = () => {
  const [hasViewedListPage, setHasViewedListPage] = useSessionStorage(
    "viewed::GamesListPage",
    false,
  );

  const [_hadViewedListPage] = useState(hasViewedListPage);
  // TODO use `hadViewedListPage` to conditionally redirect to the active game
  // if one exists

  useEffect(() => {
    if (!hasViewedListPage) {
      setHasViewedListPage(true);
    }
  }, [hasViewedListPage, setHasViewedListPage]);

  const [configQuery, scheduleQuery, gamesParticipatedQuery] =
    useGameListQueries();

  if (
    configQuery.isPending ||
    scheduleQuery.isPending ||
    gamesParticipatedQuery.isPending
  ) {
    return <LoadingScreen />;
  }

  if (
    configQuery.isError ||
    scheduleQuery.isError ||
    gamesParticipatedQuery.isError
  ) {
    return (
      <ErrorScreen
        error={
          configQuery.error ||
          scheduleQuery.error ||
          gamesParticipatedQuery.error
        }
      />
    );
  }

  const config = configQuery.data;
  const schedule = scheduleQuery.data;
  const gamesParticipated = gamesParticipatedQuery.data;

  const viewableGames = schedule.filter((game) =>
    canUserViewGame(game, gamesParticipated),
  );

  const hasViewableGames = viewableGames.length > 0;

  return (
    <Page name="GamesListPage">
      <FullscreenLayout footer={<AppFooter showDisclaimer />}>
        <Grid gap="1.5rem" width="100%" pt="1.5rem">
          <UploadedImage
            image={config.theme.splashImage}
            alt="Splash Image"
            h="16.5rem"
            w="16.5rem"
            maxWidth="100%"
            m="auto"
            objectFit="contain"
            tracking={{
              enabled: true,
              location: "GamesListPage::SplashImage",
            }}
          />
          <Flex gap="0.5rem" align="center" justify="around">
            <Dialog.Root>
              <Dialog.Trigger as={Button} size="sm" variant="outline-light">
                Game History
              </Dialog.Trigger>
              <GameHistoryDialog name="GameHistoryDialog" />
            </Dialog.Root>
          </Flex>
          <Grid gap={8} width="100%">
            {!hasViewableGames && <NoGamesMessage />}
            {viewableGames.sort(compareGamesByStatusAndDate).map((game) => (
              <GameListItem
                key={game.id}
                game={game}
                participatedGame={gamesParticipated.find(
                  (g) => g.gameId === game.id,
                )}
              />
            ))}
          </Grid>
        </Grid>
      </FullscreenLayout>
    </Page>
  );
};
